import {
  CompositeScreenProps,
  NavigatorScreenParams,
  useNavigation,
} from '@react-navigation/native';
import {
  StackScreenProps,
  TransitionPresets,
  createStackNavigator,
} from '@react-navigation/stack';
import React from 'react';
import {Platform} from 'react-native';
import {useSelector} from 'react-redux';

import {Navigators, Screens} from '../constants/navigation';
import {
  appSettingsSelector,
  updateAppSettings,
} from '../lib/redux/slices/appSettingsSlice';
import {useAppDispatch} from '../lib/redux/useAppDispatch';
import NavigationService from '../lib/utils/NavigationService';
import {WelcomeScreen} from '../screens/DeviceConnect/WelcomeScreen';
import {StackPage} from '../screens/components/StackPage';
import {
  ExclusiveMoodScreen,
  type Props as ExclusiveMoodScreenProps,
} from '../screens/controlCenter/ExclusiveMoodScreen';
import {
  EmailConfirmedScreen,
  type Props as EmailConfirmedScreenProps,
} from '../screens/login/EmailConfirmedScreen';
import {
  ForgotPasswordScreen,
  type Props as ForgotPasswordScreenProps,
} from '../screens/login/ForgetPasswordScreen';
import {
  LoginScreen,
  type Props as LoginScreenProps,
} from '../screens/login/LoginScreen';
import {
  PasswordConfirmedScreen,
  type Props as PasswordConfirmedScreenProps,
} from '../screens/login/PasswordConfirmedScreen';
import {
  PasswordResetScreen,
  type Props as PasswordResetScreenProps,
} from '../screens/login/PasswordResetScreen';
import {
  PasswordResetSentScreen,
  type Props as PasswordResetSentScreenProps,
} from '../screens/login/PasswordResetSentScreen';
import {
  RegisterScreen,
  type AuthProps as RegisterScreenProps,
} from '../screens/login/RegisterScreen';
import {ValuePropScreen} from '../screens/login/ValuePropScreen';
import {LoadingScreen} from '../screens/main/LoadingScreen';
import {colors} from '../styles';
import {CustomModalSlideFromBottomIOS} from './CustomTransitionPreset';
import {
  DownloadSharedHeatProfileStackNavigator,
  type DownloadSharedHeatProfileStackParamList,
} from './DownloadSharedHeatProfileStackNavigator';
import {
  HeatProfileEditStackNavigator,
  type HeatProfileEditStackParamList,
} from './HeatProfileEditStackNavigator';
import {
  MainStackNavigator,
  type MainStackParamList,
} from './MainStackNavigator';
import {
  defaultNavigationOptions,
  defaultNoHeaderScreenNavigation,
  modalNavigationOptions,
  withBackButton,
  withSkipButton,
} from './common';
import {WithOptionalRedirect} from './params';
import {optionsToRoute, toStartPairing} from './util';

export type RootStackParamList = {
  [Navigators.MainNavigator]: NavigatorScreenParams<MainStackParamList>;
  [Navigators.DownloadSharedHeatProfile]: NavigatorScreenParams<DownloadSharedHeatProfileStackParamList>;
  [Navigators.HeatProfileEdit]: NavigatorScreenParams<HeatProfileEditStackParamList>;

  [Screens.Loading]?: undefined;
  [Screens.Welcome]?: undefined;
  [Screens.ValueProp]?: WithOptionalRedirect;

  [Screens.Login]?: LoginScreenProps;
  [Screens.Register]?: RegisterScreenProps;
  [Screens.ForgotPassword]?: ForgotPasswordScreenProps;
  [Screens.PasswordReset]: PasswordResetScreenProps;
  [Screens.PasswordResetSent]?: PasswordResetSentScreenProps;
  [Screens.PasswordConfirmed]?: PasswordConfirmedScreenProps;
  [Screens.EmailConfirmed]: EmailConfirmedScreenProps;
  [Screens.ExclusiveMood]?: ExclusiveMoodScreenProps;

  [Screens.TermsConditions]?: LegalScreenProps;
  [Screens.PrivacyPolicy]?: LegalScreenProps;
};

export type RootStackScreenProps<T extends keyof RootStackParamList> =
  StackScreenProps<RootStackParamList, T>;

export type MainNavigatorScreenProps<T extends keyof MainStackParamList> =
  CompositeScreenProps<
    StackScreenProps<MainStackParamList, T>,
    RootStackScreenProps<keyof RootStackParamList>
  >;

interface LegalScreenProps {}

export type DownloadSharedHeatProfileNavigator<
  T extends keyof DownloadSharedHeatProfileStackParamList,
> = CompositeScreenProps<
  StackScreenProps<DownloadSharedHeatProfileStackParamList, T>,
  RootStackScreenProps<keyof RootStackParamList>
>;

export type HeatProfileEditNavigator<
  T extends keyof HeatProfileEditStackParamList,
> = CompositeScreenProps<
  StackScreenProps<HeatProfileEditStackParamList, T>,
  RootStackScreenProps<keyof RootStackParamList>
>;

const RootStack = createStackNavigator<RootStackParamList>();

export interface Props {
  initialRouteName?: keyof RootStackParamList;
}

export const RootStackNavigator = ({
  initialRouteName = Screens.Loading,
}: Props) => {
  const dispatch = useAppDispatch();

  const navigation = useNavigation();

  const {agreeAgreement} = useSelector(appSettingsSelector);

  const onValuePropSkip = () => {
    NavigationService.instance()?.resetRoot({
      index: 0,
      routes: [optionsToRoute(toStartPairing())],
    });

    if (agreeAgreement) return;

    dispatch(updateAppSettings({agreeAgreement: true}));
  };

  // We will remove the LoadingScreen from the Navigator after the first navigation (state exists)
  // This will prevent displaying the LoadingScreen after navigating back from deeplink
  const canLoad = navigation.getState() === undefined;

  return (
    <RootStack.Navigator
      screenOptions={defaultNavigationOptions}
      {...{initialRouteName}}>
      {!!canLoad && (
        <RootStack.Screen
          name={Screens.Loading}
          component={LoadingScreen}
          options={defaultNoHeaderScreenNavigation}
        />
      )}

      <RootStack.Screen
        name={Screens.Welcome}
        component={WelcomeScreen}
        options={defaultNoHeaderScreenNavigation}
      />

      <RootStack.Screen
        name={Screens.ValueProp}
        component={ValuePropScreen}
        options={{
          ...modalNavigationOptions,
          ...TransitionPresets.SlideFromRightIOS,
          ...withBackButton(),
          ...withSkipButton({
            onPress: onValuePropSkip,
            textStyle: {color: colors.white},
          }),
        }}
      />

      <RootStack.Screen
        name={Screens.Login}
        component={LoginScreen}
        options={({route}) => {
          const canSkip = route.params?.canSkip;
          const redirect = route.params?.redirect;

          return {
            ...modalNavigationOptions,
            ...TransitionPresets.SlideFromRightIOS,
            gestureEnabled: false,
            headerTransparent: false,
            ...withBackButton(),
            ...(canSkip && redirect && withSkipButton({redirect})),
          };
        }}
      />

      <RootStack.Screen
        name={Screens.Register}
        component={RegisterScreen}
        options={({route}) => {
          const redirect = route.params?.redirect;

          return {
            ...modalNavigationOptions,
            ...TransitionPresets.SlideFromRightIOS,
            gestureEnabled: false,
            headerTransparent: false,
            ...withBackButton(),
            ...(redirect && withSkipButton({redirect})),
          };
        }}
      />

      <RootStack.Screen
        name={Screens.ForgotPassword}
        component={ForgotPasswordScreen}
        options={{
          ...modalNavigationOptions,
          headerTransparent: false,
          ...withBackButton(),
        }}
      />

      <RootStack.Screen
        name={Screens.PasswordReset}
        component={PasswordResetScreen}
        options={{
          ...modalNavigationOptions,
          headerLeft: () => null,
          headerTransparent: false,
        }}
      />

      <RootStack.Screen
        name={Screens.PasswordResetSent}
        component={PasswordResetSentScreen}
        options={{
          ...modalNavigationOptions,
          headerTransparent: false,
          ...withBackButton(),
        }}
      />

      <RootStack.Screen
        name={Screens.PasswordConfirmed}
        component={PasswordConfirmedScreen}
        options={{
          ...modalNavigationOptions,
          gestureEnabled: false,
          ...withBackButton({color: colors.black}),
        }}
      />

      <RootStack.Screen
        name={Screens.EmailConfirmed}
        component={EmailConfirmedScreen}
        options={{
          ...modalNavigationOptions,
          gestureEnabled: false,
          headerShown: false,
        }}
      />

      <RootStack.Screen
        name={Screens.PrivacyPolicy}
        component={StackPage}
        options={{
          ...modalNavigationOptions,
          gestureEnabled: false,
          headerTransparent: false,
          ...withBackButton(),
        }}
      />

      <RootStack.Screen
        name={Screens.TermsConditions}
        component={StackPage}
        options={{
          ...modalNavigationOptions,
          gestureEnabled: false,
          headerTransparent: false,
          ...withBackButton(),
        }}
      />

      <RootStack.Screen
        name={Navigators.MainNavigator}
        component={MainStackNavigator}
        options={{headerShown: false}}
      />

      <RootStack.Screen
        name={Navigators.HeatProfileEdit}
        component={HeatProfileEditStackNavigator}
        options={{
          ...CustomModalSlideFromBottomIOS,
          ...defaultNavigationOptions,
          gestureEnabled: false,
          cardOverlayEnabled: true,
          headerTransparent: false,
          headerShown: false,
          cardStyle: {
            backgroundColor:
              Platform.OS !== 'android' ? '#00000033' : '#000000E6',
          },
        }}
      />

      <RootStack.Screen
        name={Navigators.DownloadSharedHeatProfile}
        component={DownloadSharedHeatProfileStackNavigator}
        options={{
          ...TransitionPresets.ModalSlideFromBottomIOS,
          ...modalNavigationOptions,
          headerShown: false,
          gestureEnabled: false,
          cardOverlayEnabled: true,
        }}
      />

      <RootStack.Screen
        name={Screens.ExclusiveMood}
        component={ExclusiveMoodScreen}
        options={{
          ...TransitionPresets.ModalSlideFromBottomIOS,
          ...defaultNavigationOptions,
          gestureEnabled: false,
          cardOverlayEnabled: true,
          headerTransparent: true,
        }}
      />
    </RootStack.Navigator>
  );
};
